// Photo compression utilities
export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.0 to 1.0
  maxSizeKB?: number; // Maximum file size in KB
  format?: 'jpeg' | 'webp' | 'png';
}

export interface CompressionResult {
  blob: Blob;
  dataUrl: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

// Default compression settings optimized for upload speed
const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 2048,
  maxHeight: 1536,
  quality: 0.85,
  maxSizeKB: 800, // Target 800KB per image for better quality
  format: 'jpeg',
};

/**
 * Compresses an image file for faster upload
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise<CompressionResult>
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const originalSize = file.size;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    if (!ctx) {
      reject(new Error('Cannot get canvas context'));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      const { width: newWidth, height: newHeight } = calculateDimensions(
        img.width,
        img.height,
        opts.maxWidth,
        opts.maxHeight
      );

      // Set canvas dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Draw and compress image
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convert to blob with compression
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          // Check if we need further compression
          if (blob.size > opts.maxSizeKB * 1024 && opts.quality > 0.1) {
            // Reduce quality and try again
            const newQuality = Math.max(0.1, opts.quality * 0.8);
            compressImage(file, { ...options, quality: newQuality })
              .then(resolve)
              .catch(reject);
            return;
          }

          // Create data URL
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result as string;
            const compressionRatio = (originalSize - blob.size) / originalSize;

            resolve({
              blob,
              dataUrl,
              originalSize,
              compressedSize: blob.size,
              compressionRatio,
            });
          };
          reader.onerror = () =>
            reject(new Error('Failed to read compressed image'));
          reader.readAsDataURL(blob);
        },
        `image/${opts.format}`,
        opts.quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));

    // Create object URL for the image and load it
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    // Clean up object URL when done
    const originalOnLoad = img.onload;
    img.onload = event => {
      URL.revokeObjectURL(objectUrl);
      if (originalOnLoad) originalOnLoad.call(img, event);
    };
  });
};

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let { width, height } = { width: originalWidth, height: originalHeight };

  // Calculate scaling factor
  const widthRatio = maxWidth / width;
  const heightRatio = maxHeight / height;
  const ratio = Math.min(widthRatio, heightRatio, 1); // Don't upscale

  width = Math.floor(width * ratio);
  height = Math.floor(height * ratio);

  return { width, height };
}

/**
 * Get optimal compression settings based on file size
 */
export const getOptimalCompressionSettings = (
  fileSizeBytes: number
): CompressionOptions => {
  const sizeMB = fileSizeBytes / (1024 * 1024);

  if (sizeMB > 8) {
    // Very large files - moderate compression for better quality
    return {
      maxWidth: 1920,
      maxHeight: 1080,
      quality: 0.75,
      maxSizeKB: 600,
      format: 'jpeg',
    };
  } else if (sizeMB > 3) {
    // Large files - light compression
    return {
      maxWidth: 2048,
      maxHeight: 1536,
      quality: 0.8,
      maxSizeKB: 700,
      format: 'jpeg',
    };
  } else {
    // Small files - minimal compression for best quality
    return {
      maxWidth: 2048,
      maxHeight: 1536,
      quality: 0.85,
      maxSizeKB: 800,
      format: 'jpeg',
    };
  }
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Convert blob to File with specified name
 */
export const blobToFile = (blob: Blob, fileName: string): File => {
  return new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  });
};
